import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Building2, Trash2, DollarSign, GraduationCap, Wallet, Bus, Users, Laptop, Shield, User, Calendar, Megaphone, MessageSquare, Lock } from 'lucide-react';

const departmentIcons = {
  'Fee': DollarSign,
  'Admissions': GraduationCap,
  'Finance': Wallet,
  'Transport': Bus,
  'HR': Users,
  'IT': Laptop,
  'Admin': Shield,
  'Principal': User,
  'Events': Calendar,
  'Digital Media': Megaphone,
  'CRM and Media': MessageSquare,
};

const departmentColors = {
  'Fee': { bg: 'bg-green-100', text: 'text-green-600', border: 'border-green-200' },
  'Admissions': { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200' },
  'Finance': { bg: 'bg-yellow-100', text: 'text-yellow-600', border: 'border-yellow-200' },
  'Transport': { bg: 'bg-orange-100', text: 'text-orange-600', border: 'border-orange-200' },
  'HR': { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-200' },
  'IT': { bg: 'bg-cyan-100', text: 'text-cyan-600', border: 'border-cyan-200' },
  'Admin': { bg: 'bg-indigo-100', text: 'text-indigo-600', border: 'border-indigo-200' },
  'Principal': { bg: 'bg-pink-100', text: 'text-pink-600', border: 'border-pink-200' },
  'Events': { bg: 'bg-rose-100', text: 'text-rose-600', border: 'border-rose-200' },
  'Digital Media': { bg: 'bg-fuchsia-100', text: 'text-fuchsia-600', border: 'border-fuchsia-200' },
  'CRM and Media': { bg: 'bg-teal-100', text: 'text-teal-600', border: 'border-teal-200' },
};

export default function DepartmentList({ departments, loading, onDelete }) {
  if (loading) {
    return <div className="text-center py-8 text-slate-600">Loading departments...</div>;
  }

  if (departments.length === 0) {
    return <div className="text-center py-8 text-slate-600">No departments found</div>;
  }

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {departments.map((dept) => {
        const Icon = departmentIcons[dept.name] || Building2;
        const colors = departmentColors[dept.name] || { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200' };
        const isPredefined = dept.is_predefined || false;
        
        return (
          <Card 
            key={dept.id} 
            className={`p-6 bg-gradient-to-br from-white to-slate-50 border-2 ${colors.border} hover:shadow-lg transition-all duration-200 ${isPredefined ? 'ring-2 ring-indigo-200' : ''}`}
            data-testid="department-card"
          >
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-lg ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                <Icon className={`w-6 h-6 ${colors.text}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-bold text-slate-900 truncate">{dept.name}</h3>
                  {isPredefined && (
                    <Lock className="w-4 h-4 text-indigo-600 flex-shrink-0" title="Predefined by Super Admin" />
                  )}
                </div>
                <p className="text-sm text-slate-600 mt-1">
                  {isPredefined ? 'Predefined Department' : 'Department'}
                </p>
              </div>
              {onDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(dept.id, dept.name, isPredefined)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid="delete-dept-button"
                  title={isPredefined ? "Remove this predefined department from your school" : "Delete department"}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
}